import { IconButton } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";

import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import dayjs from "dayjs";
import { CronogramaSGQLista } from "../../../../domain/models/cronogramaSGQ_lista";
import ItemStatus from "../../../../../../common/components/item_status";

const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return "";
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};
type Props = {
  onActionEditar: ( Id: number | null, openState: boolean) => void,
  onActionDeletar: ( Id: number | null, openState: boolean) => void,
};

export const GenerateQualidadeCronogramaSGQColumns = ({
  onActionEditar,
  onActionDeletar,
}: Props): GridColDef[] => {

  //#region  Status
  const getItemStatus = (
    terminoReal: Date | string | null | undefined,     // DataRealizadaReal
    terminoPrevisto: Date | string | null | undefined  // DataRealizada
  ): 'R' | 'EA' | 'DP' => {

    // 1. R – Quando o campo término real for preenchido 
    if (terminoReal) {
      return 'R';
    }

    // Prevenção de erro: se por acaso não existir data de término previsto, assume DP
    if (!terminoPrevisto) return 'DP';

    // Zerando as horas para comparar apenas os dias (Data de Hoje vs Data Prevista)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const plannedEnd = new Date(terminoPrevisto);
    plannedEnd.setHours(0, 0, 0, 0);

    // 2. DP – a data de hoje for menor que a data do campo término previsto
    if (today.getTime() < plannedEnd.getTime()) {
      return 'DP';
    }

    // 3. Em Atraso – a data de hoje for maior ou igual que a data do campo término previsto
    return 'EA';
  };

  const computeItemStatusTitle = (status: 'R' | 'EA' | 'DP') => {
    switch (status) {
        case 'R':
            return "R";
        case 'DP':
            return "DP";
        case 'EA':
            return "EA";
        default:
            return "?";
    }
  };

  const computeItemStatusDescription = (status: 'R' | 'EA' | 'DP') => {
    switch (status) {
        case 'R':
            return "Realizada";
        case 'DP':
            return "Dentro do Prazo";
        case 'EA':
            return "Em Atraso";
        default:
            return "?";
    }
  };

  const computeItemStatusColor = (status: 'R' | 'EA' | 'DP') => {
    switch (status) {
        case 'R':
            return "green"; 
        case 'DP':
            return "blue"; 
        case 'EA':
            return "red"; 
        default:
            return "yellow";
    }
  };
  //#endregion
  
  return [
    {
      field: "status_prazo",
      filterable: false,
      headerName: "Status",
      flex: 1,
      maxWidth: 75,
      sortable: false,
      renderCell: (params) => {
        const rowData = params.row as CronogramaSGQLista;
        
        const status = getItemStatus(
          rowData.DataRealizadaReal, // Término Real
          rowData.DataRealizada      // Término Previsto
        );
        
        const title = computeItemStatusTitle(status);
        const color = computeItemStatusColor(status);
        const description = computeItemStatusDescription(status);

        return (
          <div className="w-full h-full flex justify-center items-center">
            <ItemStatus
              title={title}
              color={color}
              description={description}
            />
          </div>
        );
      }
    },
    {
      field: "Item",
      filterable: false,
      headerName: "Nº",
      flex: 1,
      maxWidth: 60,
      sortable: false,
      renderCell: (params) => {
        return (<div className="w-full h-full flex justify-center items-center text-center text-xs py-2">
          <span>{params.value}</span>
        </div>);
      },
    },
    {
      field: "Atividade",
      filterable: false,
      headerName: "Atividade",
      flex: 1,
      minWidth: 400,
      sortable: false,
      renderCell: (params) => {
       return (<div className="break-normal w-full h-full flex justify-start items-center text-left text-xs py-2 px-2">
          <span>{params.value}</span>
        </div>);
      },

    },
    {
      field: "DataPrevista",
      filterable: false,
      headerName: "Início Previsto",
      flex: 1,
      maxWidth: 110,
      sortable: false,
      renderCell: (params) => {
        return (<span className="w-full h-full flex justify-center items-center text-center text-xs py-2">{params.value && dayjs(params.value).format("DD/MM/YYYY")}</span>);
      },
    },
    {
      field: "DataRealizada",
      filterable: false,
      headerName: "Término Previsto",
      flex: 1,
      maxWidth: 130,
      sortable: false,
      renderCell: (params) => {
        return (<span className="w-full h-full flex justify-center items-center text-center text-xs py-2">{params.value && dayjs(params.value).format("DD/MM/YYYY")}</span>);
      },
    },
    {
      field: "DataPrevistaReal",
      filterable: false,
      headerName: "Início Real",
      flex: 1,
      maxWidth: 110,
      sortable: false,
      renderCell: (params) => {
        return (<span className="w-full h-full flex justify-center items-center text-center text-xs py-2">{params.value && dayjs(params.value).format("DD/MM/YYYY")}</span>);
      },
    },
    {
      field: "DataRealizadaReal",
      filterable: false,
      headerName: "Término Real",
      flex: 1,
      maxWidth: 110,
      sortable: false,
      renderCell: (params) => {
        return (<span className="w-full h-full flex justify-center items-center text-center text-xs py-2">{params.value && dayjs(params.value).format("DD/MM/YYYY")}</span>);
      },
    },
    {
      field: "ValorPrevisto",
      filterable: false,
      headerName: "Valor Previsto",
      flex: 1,
      maxWidth: 110,
      sortable: false,
      renderCell: (params) => {
        return (<div className="w-full h-full flex justify-center items-center"><span>{formatCurrency(params.value)}</span></div>);
      },
    },
    {
      field: "ValorRealizado",
      filterable: false,
      headerName: "Valor Realizado",
      flex: 1,
      maxWidth: 130,
      sortable: false,
      renderCell: (params) => {
        return (<div className="w-full h-full flex justify-center items-center"><span>{formatCurrency(params.value)}</span></div>);
      },
    },
    {
      field: "NomeResponsavel",
      filterable: false,
      headerName: "Responsável",
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
       return (<div className="break-normal w-full h-full flex justify-start items-center text-left text-xs py-2 px-2">
          <span>{params.value}</span>
        </div>);
      },
    },
    {
    field: 'IdCronogramaSGQ',
    sortable: false,
    filterable: false,
    headerName: 'Ações',
    minWidth: 100,

    renderCell: (params) => {
      return (
        <div className="flex items-center justify-center w-full h-full gap-1">
          
          <IconButton onClick={() => onActionEditar(params.value, true)}>
            <DynamicIcon nameIcon="EditRounded" size={22} className="dark:text-white text-primary-green" />
          </IconButton>
          <IconButton onClick={() => onActionDeletar(params.value, true)} >
            <DynamicIcon nameIcon="DeleteForeverRounded" size={22} className="text-red-500" />
          </IconButton>

        </div>
      );
    }
    },
  ];
}